<script>
import bem from '@/utils/bem';
import useMobileStyle from '@/mixins/useMobileStyle';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditTitleWithLinesModal from '@/components/pageContent/modules/EditTitleWithLinesModal.vue';
import EditDescriptionModal from '@/components/pageContent/modules/EditDescriptionModal.vue';
import EditButtonModal from '@/components/pageContent/modules/EditButtonModal.vue';
import ImageUploadModal from '@/components/pageContent/modules/ImageUploadModal.vue';
import TitleWithLinesBlock from './title-with-lines-block.vue';
import CustomImage from './custom-image.vue';
import DescriptionBlock from './description-block.vue';
import CustomButton from './custom-button.vue';
import styles from './studiosBlockStyle.scss';

const b = bem('studios-block', styles);

export default {
  name: 'StudiosBlock',
  components: {
    TitleWithLinesBlock,
    CustomImage,
    DescriptionBlock,
    CustomButton,
    PencilIcon,
    EditTitleWithLinesModal,
    EditDescriptionModal,
    EditButtonModal,
    ImageUploadModal,
  },
  mixins: [useMobileStyle],
  props: {
    className: {
      type: String,
      default: '',
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  data() {
    return {
      // Main title modal
      mainTitleModalVisible: false,
      // Studio-specific modals
      studioTitleModalVisible: false,
      studioImageModalVisible: false,
      studioDescriptionModalVisible: false,
      studioButtonModalVisible: false,
      // Current editing studio index
      editingStudioIndex: null,
    };
  },
  computed: {
    b() {
      return b;
    },
    isMobile() {
      return this.isMobileView;
    },
    mainTitleList() {
      if (Array.isArray(this.data.mainTitle)) {
        return this.data.mainTitle;
      }
      if (typeof this.data.mainTitle === 'string' && this.data.mainTitle) {
        return [{
          id: 'main-title-1',
          text: this.data.mainTitle,
          textMob: this.data.mainTitle,
          color: '#FFFFFF',
          colorMob: '#FFFFFF',
        }];
      }
      return [];
    },
    currentStudio() {
      if (this.editingStudioIndex !== null && this.data.studioBlock) {
        return this.data.studioBlock[this.editingStudioIndex] || {};
      }
      return {};
    },
    currentStudioTitleList() {
      return this.getStudioTitleList(this.currentStudio, this.editingStudioIndex);
    },
  },
  methods: {
    // Main title methods
    openMainTitleModal() {
      if (this.isEditMode) {
        this.mainTitleModalVisible = true;
      }
    },
    handleMainTitleSave(values) {
      this.$emit('update', { mainTitle: values });
      this.mainTitleModalVisible = false;
    },
    handleMainTitleDelete() {
      this.$emit('update', { mainTitle: [] });
      this.mainTitleModalVisible = false;
    },
    handleMainTitleCancel() {
      this.mainTitleModalVisible = false;
    },
    // Studio title methods
    openStudioTitleModal(index) {
      if (this.isEditMode) {
        this.editingStudioIndex = index;
        this.studioTitleModalVisible = true;
      }
    },
    handleStudioTitleSave(values) {
      const studioBlock = [...(this.data.studioBlock || [])];
      if (studioBlock[this.editingStudioIndex]) {
        studioBlock[this.editingStudioIndex] = {
          ...studioBlock[this.editingStudioIndex],
          title: values,
        };
        this.$emit('update', { studioBlock });
      }
      this.studioTitleModalVisible = false;
      this.editingStudioIndex = null;
    },
    handleStudioTitleDelete() {
      const studioBlock = [...(this.data.studioBlock || [])];
      if (studioBlock[this.editingStudioIndex]) {
        studioBlock[this.editingStudioIndex] = {
          ...studioBlock[this.editingStudioIndex],
          title: [],
        };
        this.$emit('update', { studioBlock });
      }
      this.studioTitleModalVisible = false;
      this.editingStudioIndex = null;
    },
    handleStudioTitleCancel() {
      this.studioTitleModalVisible = false;
      this.editingStudioIndex = null;
    },
    // Studio image methods
    openStudioImageModal(index) {
      if (this.isEditMode) {
        this.editingStudioIndex = index;
        this.studioImageModalVisible = true;
      }
    },
    handleStudioImageSave(values) {
      const studioBlock = [...(this.data.studioBlock || [])];
      if (studioBlock[this.editingStudioIndex]) {
        studioBlock[this.editingStudioIndex] = {
          ...studioBlock[this.editingStudioIndex],
          imageSrc: values.image || studioBlock[this.editingStudioIndex].imageSrc,
          imageMobileSrc: values.imageMobile || studioBlock[this.editingStudioIndex].imageMobileSrc,
          imageText: values.imageText !== undefined
            ? values.imageText
            : studioBlock[this.editingStudioIndex].imageText,
          imageTextMobile: values.imageTextMobile !== undefined
            ? values.imageTextMobile
            : studioBlock[this.editingStudioIndex].imageTextMobile,
        };
        this.$emit('update', { studioBlock });
      }
      this.studioImageModalVisible = false;
      this.editingStudioIndex = null;
    },
    handleStudioImageClose() {
      this.studioImageModalVisible = false;
      this.editingStudioIndex = null;
    },
    handleStudioImageDelete() {
      const studioBlock = [...(this.data.studioBlock || [])];
      if (studioBlock[this.editingStudioIndex]) {
        studioBlock[this.editingStudioIndex] = {
          ...studioBlock[this.editingStudioIndex],
          imageSrc: '',
          imageMobileSrc: '',
          imageText: '',
          imageTextMobile: '',
        };
        this.$emit('update', { studioBlock });
      }
      this.studioImageModalVisible = false;
      this.editingStudioIndex = null;
    },
    // Studio description methods
    openStudioDescriptionModal(index) {
      if (this.isEditMode) {
        this.editingStudioIndex = index;
        this.studioDescriptionModalVisible = true;
      }
    },
    handleStudioDescriptionSave(values) {
      const studioBlock = [...(this.data.studioBlock || [])];
      if (studioBlock[this.editingStudioIndex]) {
        studioBlock[this.editingStudioIndex] = {
          ...studioBlock[this.editingStudioIndex],
          description: values.description,
          descriptionMobile: values.descriptionMobile,
        };
        this.$emit('update', { studioBlock });
      }
      this.studioDescriptionModalVisible = false;
      this.editingStudioIndex = null;
    },
    handleStudioDescriptionClose() {
      this.studioDescriptionModalVisible = false;
      this.editingStudioIndex = null;
    },
    handleStudioDescriptionDelete() {
      const studioBlock = [...(this.data.studioBlock || [])];
      if (studioBlock[this.editingStudioIndex]) {
        studioBlock[this.editingStudioIndex] = {
          ...studioBlock[this.editingStudioIndex],
          description: '',
          descriptionMobile: '',
        };
        this.$emit('update', { studioBlock });
      }
      this.studioDescriptionModalVisible = false;
      this.editingStudioIndex = null;
    },
    // Studio button methods
    openStudioButtonModal(index) {
      if (this.isEditMode) {
        this.editingStudioIndex = index;
        this.studioButtonModalVisible = true;
      }
    },
    handleStudioButtonSave(values) {
      const studioBlock = [...(this.data.studioBlock || [])];
      if (studioBlock[this.editingStudioIndex]) {
        studioBlock[this.editingStudioIndex] = {
          ...studioBlock[this.editingStudioIndex],
          buttonText: values.buttonText,
          buttonTextMobile: values.buttonTextMobile,
          buttonLink: values.buttonLink,
        };
        this.$emit('update', { studioBlock });
      }
      this.studioButtonModalVisible = false;
      this.editingStudioIndex = null;
    },
    handleStudioButtonDelete() {
      const studioBlock = [...(this.data.studioBlock || [])];
      if (studioBlock[this.editingStudioIndex]) {
        studioBlock[this.editingStudioIndex] = {
          ...studioBlock[this.editingStudioIndex],
          buttonText: '',
          buttonTextMobile: '',
          buttonLink: '',
        };
        this.$emit('update', { studioBlock });
      }
      this.studioButtonModalVisible = false;
      this.editingStudioIndex = null;
    },
    handleStudioButtonClose() {
      this.studioButtonModalVisible = false;
      this.editingStudioIndex = null;
    },
    // Helper methods
    getStudioTitleList(studio, index) {
      if (Array.isArray(studio.title)) {
        return studio.title;
      }
      if (typeof studio.title === 'string' && studio.title) {
        const isDark = index % 2 === 0;
        return [{
          id: `studio-title-${index}`,
          text: studio.title,
          textMob: studio.title,
          color: isDark ? '#FFFFFF' : '#293241',
          colorMob: isDark ? '#FFFFFF' : '#293241',
        }];
      }
      return [];
    },
  },
};
</script>

<template>
  <article :class="b({ mix: className, isMobileView, isEditMode })">
    <div :class="b('main-title-wrap', { isMobileView, isEditMode })">
      <div
        :class="b('main-title', { isMobileView, editable: isEditMode })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openMainTitleModal"
        @keyup.enter="openMainTitleModal"
        @keyup.space="openMainTitleModal"
      >
        <title-with-lines-block
          :is-mobile="isMobile"
          :is-mobile-view="isMobileView"
          :text-list="mainTitleList"
        />
        <PencilIcon v-if="isEditMode" :class="b('edit-icon')" color="#FFFFFF" :size="20" />
      </div>
    </div>
    <div
      v-for="(studio, index) in data.studioBlock"
      :key="index"
      :class="b('studio-block', { dark: index % 2 === 0, isMobileView })"
    >
      <div :class="b('title-wrap', { isMobileView, editable: isEditMode })">
        <div
          :class="b('title', { isMobileView, editable: isEditMode })"
          :role="isEditMode ? 'button' : undefined"
          :tabindex="isEditMode ? 0 : undefined"
          @click="openStudioTitleModal(index)"
          @keyup.enter="openStudioTitleModal(index)"
          @keyup.space="openStudioTitleModal(index)"
        >
          <title-with-lines-block
            :is-mobile="isMobile"
            :is-mobile-view="isMobileView"
            :text-list="getStudioTitleList(studio, index)"
          />
          <PencilIcon
            v-if="isEditMode"
            :class="b('edit-icon')"
            :color="index % 2 === 0 ? '#FFFFFF' : '#293241'"
            :size="20"
          />
        </div>
      </div>

      <div
        :class="b('image-wrapper', { isMobileView, editable: isEditMode })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openStudioImageModal(index)"
        @keyup.enter="openStudioImageModal(index)"
        @keyup.space="openStudioImageModal(index)"
      >
        <custom-image
          v-if="(studio.imageMobileSrc || studio.imageSrc)"
          :class="b('image', { isMobileView })"
          :image-uri="isMobile ? studio.imageMobileSrc || studio.imageSrc : studio.imageSrc"
          alt="studio-image"
          :without-shadow="true"
          :with-gradient="true"
          :text="isMobile ? '' : studio.imageText"
          :is-dark-gradient="index % 2 === 0"
          :is-mobile-view="isMobileView"
        />
        <div
          v-else
          :class="b('image-placeholder', { isMobileView })"
        >
          <span :class="b('placeholder-plus')">+</span>
          <span :class="b('placeholder-text')">CLICK TO UPLOAD</span>
        </div>
      </div>

      <div
        v-if="isMobile"
        :class="b('image-text-wrap', {
          dark: index % 2 === 0,
          isMobileView,
          editable: isEditMode,
        })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openStudioImageModal(index)"
        @keyup.enter="openStudioImageModal(index)"
        @keyup.space="openStudioImageModal(index)"
      >
        <div :class="b('image-text', { dark: index % 2 === 0, isMobileView })">
          {{ studio.imageTextMobile }}
        </div>
        <PencilIcon
          v-if="isEditMode"
          :class="b('edit-icon')"
          :color="index % 2 === 0 ? '#FFFFFF' : '#293241'"
          :size="18"
        />
      </div>

      <div
        v-if="studio.description || isEditMode"
        :class="b('description-wrap', { editable: isEditMode, isMobileView })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openStudioDescriptionModal(index)"
        @keyup.enter="openStudioDescriptionModal(index)"
        @keyup.space="openStudioDescriptionModal(index)"
      >
        <description-block
          v-if="studio.description"
          :class="b('description', { isMobileView })"
          :is-white="index % 2 === 0"
          :is-mobile-view="isMobileView"
        >
          {{ isMobile && studio.descriptionMobile ? studio.descriptionMobile : studio.description }}
        </description-block>
        <div v-else-if="isEditMode" :class="b('add-field')">
          + Add description
        </div>
        <PencilIcon
          v-if="isEditMode && studio.description"
          :class="b('edit-icon')"
          :color="index % 2 === 0 ? '#FFFFFF' : '#293241'"
          :size="20"
        />
      </div>

      <div
        v-if="studio.buttonText || isEditMode"
        :class="b('button-wrap', { editable: isEditMode })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openStudioButtonModal(index)"
        @keyup.enter="openStudioButtonModal(index)"
        @keyup.space="openStudioButtonModal(index)"
      >
        <custom-button
          v-if="studio.buttonText"
          :class="b('button', { isMobileView })"
          :button-link="studio.buttonLink"
          :button-func="studio.buttonFunc"
          :with-line="isMobile"
          :is-mobile-view="isMobileView"
        >
          {{ isMobile && studio.buttonTextMobile ? studio.buttonTextMobile : studio.buttonText }}
          <PencilIcon
            v-if="isEditMode"
            :class="b('edit-icon', { inline: true })"
            color="#293241"
            :size="16"
          />
        </custom-button>
        <div v-else-if="isEditMode" :class="b('add-field')">
          + Add button
        </div>
      </div>
    </div>

    <!-- Main Title Modal -->
    <EditTitleWithLinesModal
      :visible="mainTitleModalVisible"
      :data="mainTitleList"
      :show-delete="mainTitleList.length > 0"
      @save="handleMainTitleSave"
      @cancel="handleMainTitleCancel"
      @delete="handleMainTitleDelete"
    />

    <!-- Studio Title Modal -->
    <EditTitleWithLinesModal
      :visible="studioTitleModalVisible"
      :data="currentStudioTitleList"
      :show-delete="currentStudioTitleList.length > 0"
      @save="handleStudioTitleSave"
      @cancel="handleStudioTitleCancel"
      @delete="handleStudioTitleDelete"
    />

    <!-- Studio Image Modal -->
    <ImageUploadModal
      :visible="studioImageModalVisible"
      :image="currentStudio.imageSrc || ''"
      :image-mobile="currentStudio.imageMobileSrc || ''"
      :image-text="currentStudio.imageText || ''"
      :image-text-mobile="currentStudio.imageTextMobile || ''"
      :with-image-text="true"
      :show-delete="!!currentStudio.imageSrc"
      @close="handleStudioImageClose"
      @save="handleStudioImageSave"
      @delete="handleStudioImageDelete"
    />

    <!-- Studio Description Modal -->
    <EditDescriptionModal
      :visible="studioDescriptionModalVisible"
      :description="currentStudio.description || ''"
      :description-mobile="currentStudio.descriptionMobile || ''"
      @close="handleStudioDescriptionClose"
      @save="handleStudioDescriptionSave"
      @delete="handleStudioDescriptionDelete"
    />

    <!-- Studio Button Modal -->
    <EditButtonModal
      :visible="studioButtonModalVisible"
      :button-text="currentStudio.buttonText || ''"
      :button-text-mobile="currentStudio.buttonTextMobile || ''"
      :button-link="currentStudio.buttonLink || ''"
      @close="handleStudioButtonClose"
      @save="handleStudioButtonSave"
      @delete="handleStudioButtonDelete"
    />
  </article>
</template>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/media';

.studios-block {
  &__add-field {
    display: inline-flex;
    align-items: center;
    padding: 8px 16px;
    border: 1px dashed #d9d9d9;
    border-radius: 4px;
    color: #666;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 14px;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(8) vw-full(16);
      border-radius: vw-full(4);
      font-size: vw-full(14);
    }

    &:hover {
      border-color: #1890ff;
      color: #1890ff;
    }
  }
}
</style>
